import { IVideo } from '../../pages/admin/videos/hooks/useFetchVideos';
import { BRAND_PROJECT_STATUSES } from '../../pages/videoProduction/helper';

/**
 * The derive-once status helper (design handoff §02). Status is a 3-state
 * mutually-exclusive pipeline; Demo and Non-project are FLAGS, not statuses.
 * Consumed by the card, the overflow menu, and the download button — never
 * re-derive `!hasFile` / `final` checks at a call site.
 */
export type VideoStatusKey = 'pending' | 'production' | 'unapproved' | 'approved';

export interface VideoStatus {
  key: VideoStatusKey;
  label: string;
  hasFile: boolean;
}

export const hasVideoFile = (video?: Partial<IVideo> | null): boolean =>
  !!(video && (video.key || video.s3Location));

/**
 * `project` is the surface's project when it has one (Downloads tab, share
 * pages); list feeds carry a trimmed `video.project` instead. Either may be
 * missing, in which case an empty slot falls back to In Production.
 */
export function getVideoStatus(video?: Partial<IVideo> | null, project?: { status?: string | null } | null): VideoStatus {
  const hasFile = hasVideoFile(video);
  if (!hasFile) {
    // An empty slot on a project that is still awaiting approval is not in
    // production yet — nothing has been scheduled, let alone filmed.
    const projectStatus = project?.status ?? (video as any)?.project?.status;
    if (projectStatus === BRAND_PROJECT_STATUSES.PENDING) return { key: 'pending', label: 'Pending', hasFile };
    return { key: 'production', label: 'In Production', hasFile };
  }
  if (video?.final === false) return { key: 'unapproved', label: 'Unapproved', hasFile };
  // final === true, plus legacy library files that predate the approval flow
  // (final == null but a file exists — delivered/published, downloadable).
  return { key: 'approved', label: 'Approved', hasFile };
}

export const isDemo = (video?: Partial<IVideo> | null): boolean => !!video?.demo;

/**
 * Non-project flag — only when the payload actually carries the projectId
 * column (list endpoints do; share-page adapters don't, and must not show
 * a false flag).
 */
export const isNonProject = (video?: Partial<IVideo> | null): boolean =>
  !!video && 'projectId' in video && video.projectId == null && !video.project;

/** Display title: title || name || key, run through formatVideoName (§13.1 —
 * a real backend title field is ticketed; until then the filename line is the
 * disambiguator). */
/** Basenames that mean "no file", not a real name. A row with no key still
 *  carries a DERIVED `location` that ends in "null", so an In Production video
 *  would otherwise render `null` as its filename. */
const NON_NAMES = new Set(['', 'null', 'undefined']);

export function getVideoFilename(video?: Partial<IVideo> | null): string {
  // Prefer the authoritative key; `location` is a derived CDN URL and is the
  // least trustworthy source for a name.
  const source = video?.key || video?.s3Location || video?.location || '';
  if (!source) return '';
  const base = source.substring(source.lastIndexOf('/') + 1).split('?')[0];
  return NON_NAMES.has(base.trim().toLowerCase()) ? '' : base;
}
